import { motion } from "framer-motion";
import { FileText, Download, Eye, Calendar, FileCheck, Users, AlertCircle, Phone } from "lucide-react";
import { Layout } from "@/components/Layout";
import { regulations } from "@/lib/index";
import { fadeInUp, staggerContainer, staggerItem, springPresets } from "@/lib/motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

export default function Regulations() {
  const quickLinks = [
    { icon: Calendar, title: "التقويم الأكاديمي", description: "مواعيد الفصول الدراسية والعطلات" },
    { icon: FileCheck, title: "جدول الامتحانات", description: "مواعيد الامتحانات النهائية" },
    { icon: FileText, title: "نماذج طلابية", description: "نماذج الطلبات والإجراءات" },
    { icon: Users, title: "شؤون الطلاب", description: "خدمات ومساعدة الطلاب" },
  ];

  const importantRules = [
    {
      title: "نسبة الحضور المطلوبة",
      value: "75%",
      description: "الحد الأدنى للحضور لدخول الامتحان النهائي",
      color: "text-chart-1",
    },
    {
      title: "درجة النجاح",
      value: "50%",
      description: "الحد الأدنى للنجاح في المقرر الدراسي",
      color: "text-chart-3",
    },
    {
      title: "فترة الانسحاب",
      value: "6 أسابيع",
      description: "آخر موعد للانسحاب من المقرر دون رسوب",
      color: "text-chart-4",
    },
    {
      title: "إعادة الامتحان",
      value: "مرة واحدة",
      description: "عدد المرات المسموح بها لإعادة الامتحان",
      color: "text-chart-5",
    },
  ];

  const getTypeBadgeVariant = (type: string) => {
    switch (type) {
      case "لائحة":
        return "default";
      case "دليل":
        return "secondary";
      case "نموذج":
        return "outline";
      default:
        return "default";
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-background">
        <motion.section
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="relative py-24 bg-gradient-to-b from-primary/5 via-background to-background"
        >
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={springPresets.bouncy}
                className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 mb-6"
              >
                <FileText className="w-10 h-10 text-primary" />
              </motion.div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">
                اللوائح والأدلة الأكاديمية
              </h1>
              <p className="text-lg text-muted-foreground">
                جميع اللوائح والأدلة والنماذج الأكاديمية المعتمدة من الكلية
              </p>
            </div>
          </div>
        </motion.section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
              className="max-w-6xl mx-auto"
            >
              <motion.div variants={staggerItem} className="mb-12">
                <h2 className="text-3xl font-bold mb-2 text-foreground">الوثائق الرسمية</h2>
                <p className="text-muted-foreground">اللوائح والأدلة المعتمدة للعام الأكاديمي 2026</p>
              </motion.div>

              <div className="grid md:grid-cols-2 gap-6 mb-16">
                {regulations.map((regulation, index) => (
                  <motion.div key={regulation.id} variants={staggerItem}>
                    <Card className="h-full hover:shadow-lg transition-shadow duration-200">
                      <CardHeader>
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <CardTitle className="text-xl mb-2">{regulation.title}</CardTitle>
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge variant={getTypeBadgeVariant(regulation.type)}>
                                {regulation.type}
                              </Badge>
                              <span className="text-sm text-muted-foreground">
                                {regulation.pages} صفحة
                              </span>
                              <span className="text-sm text-muted-foreground">•</span>
                              <span className="text-sm text-muted-foreground">{regulation.year}</span>
                            </div>
                          </div>
                          <FileText className="w-8 h-8 text-primary flex-shrink-0" />
                        </div>
                        <CardDescription className="text-base leading-relaxed">
                          {regulation.summary}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="flex gap-3">
                          <Button variant="default" className="flex-1">
                            <Download className="w-4 h-4 ml-2" />
                            تحميل PDF
                          </Button>
                          <Button variant="outline" className="flex-1">
                            <Eye className="w-4 h-4 ml-2" />
                            عرض
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>

              <motion.div variants={staggerItem} className="mb-12">
                <h2 className="text-3xl font-bold mb-2 text-foreground">روابط سريعة</h2>
                <p className="text-muted-foreground">الوصول السريع للخدمات والموارد الأكاديمية</p>
              </motion.div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
                {quickLinks.map((link, index) => {
                  const Icon = link.icon;
                  return (
                    <motion.div key={index} variants={staggerItem}>
                      <Card className="h-full hover:shadow-lg transition-all duration-200 hover:scale-[1.02] cursor-pointer">
                        <CardHeader className="text-center">
                          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 mx-auto mb-3">
                            <Icon className="w-7 h-7 text-primary" />
                          </div>
                          <CardTitle className="text-lg">{link.title}</CardTitle>
                          <CardDescription className="text-sm">{link.description}</CardDescription>
                        </CardHeader>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>

              <motion.div variants={staggerItem} className="mb-12">
                <h2 className="text-3xl font-bold mb-2 text-foreground">قواعد مهمة</h2>
                <p className="text-muted-foreground">أهم القواعد والشروط الأكاديمية التي يجب معرفتها</p>
              </motion.div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
                {importantRules.map((rule, index) => (
                  <motion.div key={index} variants={staggerItem}>
                    <Card className="h-full text-center">
                      <CardHeader>
                        <div className={`text-5xl font-bold mb-2 ${rule.color}`}>{rule.value}</div>
                        <CardTitle className="text-lg mb-2">{rule.title}</CardTitle>
                        <CardDescription className="text-sm leading-relaxed">
                          {rule.description}
                        </CardDescription>
                      </CardHeader>
                    </Card>
                  </motion.div>
                ))}
              </div>

              <motion.div variants={staggerItem}>
                <Card className="bg-gradient-to-br from-primary/5 to-accent/5 border-primary/20">
                  <CardHeader className="text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mx-auto mb-4">
                      <AlertCircle className="w-8 h-8 text-primary" />
                    </div>
                    <CardTitle className="text-2xl mb-2">هل تحتاج إلى مساعدة؟</CardTitle>
                    <CardDescription className="text-base mb-6">
                      فريق شؤون الطلاب جاهز للإجابة على استفساراتك حول اللوائح والإجراءات الأكاديمية
                    </CardDescription>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                      <Button size="lg" className="min-w-[200px]">
                        <Phone className="w-5 h-5 ml-2" />
                        اتصل بنا
                      </Button>
                      <Button size="lg" variant="outline" className="min-w-[200px]">
                        <FileText className="w-5 h-5 ml-2" />
                        إرسال استفسار
                      </Button>
                    </div>
                  </CardHeader>
                </Card>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
